import React from "react";
import { Button, Spinner, Title } from "@yoast/ui-library";
import useRequest from "../../reactHooks/useRequest";
import ErrorDisplay from "../../errors/ErrorDisplay";
import { defineMessages, FormattedMessage, injectIntl, intlShape } from "react-intl";
import PropTypes from "prop-types";
import ButtonsContainer from "../general/ButtonsContainer";
import * as styles from "./styles.module.scss";

import { ArrowTopRightOnSquareIcon } from "@heroicons/react/20/solid";


/**
 * @typedef {import("../../../../nestjs-server/src/domains/Licensing/UserInterface/dtos/AcceptInvitationDto")
 * .AcceptInvitationResponseDto} AcceptInvitationResponse
 */

const messages = defineMessages( {
	expired: {
		id: "acceptEmailInvite.expiredLink",
		defaultMessage: "The link you clicked has expired.",
	},
	invalid: {
		id: "acceptEmailInvite.invalidLink",
		defaultMessage: "Invalid link.",
	},
	genericError: {
		id: "acceptEmailInvite.genericError",
		defaultMessage: "An error occurred while accepting the invitation. Please try again later.",
	},
	activatedTitle: {
		id: "acceptEmailInvite.activatedTitle",
		defaultMessage: "You're all set! 🎉",
	},
	activatedDescriptionGoogleDocs: {
		id: "acceptEmailInvite.activatedDescription",
		defaultMessage: "Yoast SEO for Google Docs has been successfully linked to your Google account. " +
			"Start optimizing your content right now inside Google Docs. Get real-time feedback and improve your SEO!",
	},
	installButton: {
		id: "acceptEmailInvite.installButton",
		defaultMessage: "Install {invitableProduct}!",
		values: { invitableProduct: "Yoast SEO for Google Docs" },
	},
} );


export const AcceptEmailInvite = injectIntl( ( { subscriptionId, inviteId, inviteToken, intl } ) => {
	/** @type {{
	 *  data: AcceptInvitationResponse | [],
	 *  isLoading: boolean,
	 *  error: Object,
	 *  reload: Function,
	 * }} */
	const request = useRequest(
		`v1/access/${ subscriptionId }/invites/${ inviteId }/accept`,
		"POST",
		{
			verificationToken: inviteToken,
		},
	);


	let errorToDisplay = null;
	if ( request.isLoading === false && request.data?.success === false && request.data?.errorCode ) {
		switch ( request.data?.errorCode ) {
			case "EXPIRED_VERIFICATION_TOKEN":
				errorToDisplay = { type: "error", message: intl.formatMessage( messages.expired ) };
				break;
			case "INVALID_VERIFICATION_TOKEN":
				errorToDisplay = { type: "error", message: intl.formatMessage( messages.invalid ) };
				break;
			default:
				errorToDisplay = { type: "error", message: intl.formatMessage( messages.genericError ) };
				break;
		}
	} else if ( request.isLoading === false && request.error ) {
		switch ( request.error.statusCode ) {
			case 404:
				errorToDisplay = { type: "error", message: intl.formatMessage( messages.invalid ) };
				break;
			default:
				errorToDisplay = { type: "error", message: intl.formatMessage( messages.genericError ) };
				break;
		}
	}

	if ( request.data?.success !== true ) {
		return <div>
			<ErrorDisplay error={ errorToDisplay } />
			{ request.isLoading && <>Verifying your email address...<Spinner size={ "8" } /></> }
			{ request.error && <ButtonsContainer>
				<Button
					onClick={ request.reload }
					disabled={ request.isLoading }
					isLoading={ request.isLoading }
				>
					Retry
				</Button>
			</ButtonsContainer> }
		</div>;
	}

	// Only account for a single installation URL for now The API gives all available links for all included products.
	const installLink = request.data?.installationUrls?.shift();

	return <div className={ "yst-max-w-xl" }>
		<Title><FormattedMessage { ...messages.activatedTitle } /></Title>
		<p className={ "yst-mt-6" }>
			<FormattedMessage { ...messages.activatedDescriptionGoogleDocs } />
		</p>
		{
			// On click, navigate to the installation URL in a new tab.
			installLink && <Button onClick={ () => window.open( installLink, "_blank" ) } className={ "yst-mt-6" }>
				<span className={ styles.button }>
					<FormattedMessage { ...messages.installButton } />
					<ArrowTopRightOnSquareIcon className={ styles.externalLinkIcon } />
				</span>
			</Button>
		}
	</div>;
} );

AcceptEmailInvite.propTypes = {
	subscriptionId: PropTypes.string.isRequired,
	inviteId: PropTypes.string.isRequired,
	inviteToken: PropTypes.string.isRequired,
	intl: intlShape.isRequired,
};
